import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ProfitSharingPerformancePage = () => {
  const navigate = useNavigate();
  const { referralCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPerformance();
  }, [referralCode]);

  const fetchPerformance = async () => {
    setLoading(true);
    setError(null);
    try {
      // Reuse the same referral performance endpoint
      const response = await axios.get(`${API}/admin/referral-performance/${referralCode}`, { withCredentials: true });
      setData(response.data);
    } catch (err) {
      console.error('Failed to load profit sharing performance:', err);
      if (err.response?.status === 403) {
        setError('Admin access required to view this page.');
      } else if (err.response?.status === 401) {
        setError('Please login to access this page.');
      } else {
        setError('Unable to load profit sharing performance. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin/profit-sharing-registrations')} 
              className="p-2 hover:bg-white/20 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Profit Sharing Performance – {referralCode}</h1>
                <p className="text-amber-100 text-sm">Users who registered using this profit sharing referral link / QR code</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
            <span className="ml-3 text-gray-600">Loading profit sharing performance...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchPerformance} variant="outline">
              Retry
            </Button>
          </div>
        ) : (
          <>
            {/* Summary Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Total Registrations via this Referral</p>
                <p className="text-5xl font-bold text-amber-600" data-testid="total-count">
                  {data?.total_registrations || 0}
                </p>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {data?.registrations?.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No users have registered using this referral yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          User ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          User Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Registered Phone Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Registered At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data?.registrations?.map((reg, index) => (
                        <tr
                          key={reg.user_id || index}
                          className="hover:bg-gray-50 transition-colors"
                          data-testid={`performance-row-${index}`}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600 font-mono">{reg.user_id?.slice(0, 8)}...</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="font-medium text-gray-900">{reg.name || 'User'}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-700">{reg.phone || '-'}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{formatDate(reg.created_at)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            {data?.registrations?.length > 0 && (
              <div className="mt-6 text-center text-sm text-gray-500">
                Showing {data.registrations.length} user{data.registrations.length !== 1 ? 's' : ''} registered via {referralCode}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfitSharingPerformancePage;
